import UAParser from 'ua-parser-js';
import _ from 'lodash';
import { List } from 'immutable';

import { NotFoundError } from '../lib/APIClient';
import AuthenticatedAPIClient from '../lib/AuthenticatedAPIClient';
import { resolve, resolveAll } from '../helpers/LinkMapper';
import { FulfilmentType, JourneyDirections } from '../constants/AppConstants';
import appVersion from '../appVersion';
import Order from '../records/Order';
import AppliedVoucher from '../records/order/AppliedVoucher';
import Voucher from '../records/customer/Voucher';
import DeliveryOption from '../records/DeliveryOption';
import AddressService from './AddressService';
import CallingPointsActions from '../actions/CallingPointsActions';
import UserStore from '../stores/UserStore';
import OrderStore from '../stores/OrderStore';
import SmartcardStore from '../stores/SmartcardStore';
import PromoCodeStore from '../stores/PromoCodeStore';
import FeatureFlagStore from '../stores/FeatureFlagStore';
import { throwCorrectOrderError, InteropError, EmptyOrderResponseError } from '../errors/Order';
import { SmartcardStatus } from '../constants/AppConstants';
import Changeover from '../records/order/Changeover';
import FamilyVerificationStore from '../stores/FamilyVerificationStore';

const dataUrl = '/orders';

const getEmailAddress = ({ hasTransPennineStaffRoverTrip, loggedIn, userEmail }) => {
    const enableTPERover = FeatureFlagStore.get('enableTPERover');
    const enableTPEStaffRover = FeatureFlagStore.get('enableTPEStaffRover');

    const familyVerificationEmail = FamilyVerificationStore.getEmail();

    // Put Customer User Group email instead of logged in email for TransPennine Staff Rover
    if (hasTransPennineStaffRoverTrip && enableTPERover && enableTPEStaffRover && familyVerificationEmail) {
        return familyVerificationEmail;
    }

    if (loggedIn) {
        return userEmail;
    }

    return null;
};

function serialisePlusBus(plusBus) {

    return {
        outward: {
            origin: plusBus.getIn(['outward', 'origin', 'id'], null),
            destination: plusBus.getIn(['outward', 'destination', 'id'], null)
        },
        'return': {
            origin: plusBus.getIn(['return', 'origin', 'id'], null),
            destination: plusBus.getIn(['return', 'destination', 'id'], null)
        }
    };
}

function _applySplitOriginAdjustment(newLeg, leg, legCallingPoints, callingPointIds, callingPoints, splitOriginNlc) {
    if (
        !legCallingPoints ||
        !legCallingPoints.length ||
        !splitOriginNlc ||
        !callingPoints.isLegValidForSplitOrigin(leg, splitOriginNlc)
    ) {
        return;
    }

    const customOriginInCallingPointsIdx = legCallingPoints.findIndex((callingPoint) => {
        const hasTime = callingPoint?.time;
        const station = callingPoint?.station;
        return hasTime && station?.nlc === splitOriginNlc;
    });

    if (customOriginInCallingPointsIdx < 0) {
        return;
    }

    const customOriginInCallingPoints = legCallingPoints[customOriginInCallingPointsIdx];
    const customStation = customOriginInCallingPoints?.station;
    const customTime = customOriginInCallingPoints?.time;
    const adjustedTime = customTime?.adjustedTime;
    if (customStation?.id) {
        newLeg.origin = customStation.id;
        newLeg.trainOrigin = customStation.id;
    }
    if (adjustedTime) {
        newLeg.departure = adjustedTime.formatISOForAPI();
    }
    newLeg.callingPoints = callingPointIds.slice(customOriginInCallingPointsIdx);
    newLeg.split = true;
}

function _applySplitDestinationAdjustment(newLeg, leg, legCallingPoints, callingPointIds, callingPoints, splitDestinationNlc) {
    if (
        !legCallingPoints ||
        !legCallingPoints.length ||
        !splitDestinationNlc ||
        !callingPoints.isLegValidForSplitDestination(leg, splitDestinationNlc)
    ) {
        return;
    }

    const customDestinationInCallingPointsIdx = legCallingPoints.findIndex((callingPoint) => {
        const hasTime = callingPoint?.time;
        const station = callingPoint?.station;
        return hasTime && station?.nlc === splitDestinationNlc;
    });

    if (customDestinationInCallingPointsIdx < 0) {
        return;
    }

    const customDestinationInCallingPoints = legCallingPoints[customDestinationInCallingPointsIdx];
    const customStation = customDestinationInCallingPoints?.station;
    const customTime = customDestinationInCallingPoints?.time;
    const scheduledTime = customTime?.scheduledTime;
    if (customStation?.id) {
        newLeg.destination = customStation.id;
        newLeg.trainDestination = customStation.id;
    }
    if (scheduledTime) {
        newLeg.arrival = scheduledTime.formatISOForAPI();
    }
    newLeg.callingPoints = callingPointIds.slice(0, customDestinationInCallingPointsIdx + 1);
    newLeg.split = true;
}

function _getSplitLegRange(newLegs, splitOriginNlc, splitDestinationNlc) {
    const journeyOriginIndex = splitOriginNlc
        ? newLegs.findIndex((resultLeg) => {
            if (!resultLeg || !resultLeg.origin) {
                return false;
            }
            const isBeforeSplit = !!resultLeg.transferTime &&
                resultLeg.destination && resultLeg.destination.endsWith(splitOriginNlc);
            return isBeforeSplit || resultLeg.origin.endsWith(splitOriginNlc);
        })
        : 0;

    const journeyDestinationIndex = splitDestinationNlc
        ? newLegs.reduce((found, resultLeg, idx) => {
            if (!resultLeg || !resultLeg.destination) {
                return found;
            }
            // A leg *starting* at the split destination with a transfer means the split
            // boundary sits between it and the previous leg, so the range ends one leg
            // earlier - not on this leg itself (which belongs to the next split segment).
            const isAfterSplit = !!resultLeg.transferTime &&
                resultLeg.origin && resultLeg.origin.endsWith(splitDestinationNlc);
            if (isAfterSplit) {
                return idx - 1;
            }
            return resultLeg.destination.endsWith(splitDestinationNlc) ? idx : found;
        }, -1)
        : -1;

    const collectionSize = newLegs.size !== null ? newLegs.size : newLegs.length;
    const startIndex = journeyOriginIndex >= 0 ? journeyOriginIndex : 0;
    const endIndex = journeyDestinationIndex >= 0 ? journeyDestinationIndex : collectionSize - 1;
    return { startIndex, endIndex };
}

export function serializeLegsForSplitTickets(legs, callingPoints, splitOriginDestination) {
    const splitOriginNlc = splitOriginDestination ? splitOriginDestination.originNlc : null;
    const splitDestinationNlc = splitOriginDestination ? splitOriginDestination.destinationNlc : null;

    const newLegs = legs.map((leg) => {
        const origin = callingPoints.getLegOrigin(leg);
        const destination = callingPoints.getLegDestination(leg);
        const legCallingPoints = callingPoints.getLegCallingPoints(leg);
        const trainOriginStation = callingPoints.getTrainOriginStation(leg);
        const trainDestinationStation = callingPoints.getTrainDestinationStation(leg);
        const legOriginStop = leg?.origin?.first();
        const legDestinationStop = leg?.destination?.first();
        const serviceDetails = leg?.serviceDetails ?? {};
        const callingPointIds = legCallingPoints?.map((callingPoint) => {
            const station = callingPoint?.station;
            return station ? station.id : null;
        });

        if (!origin || !destination) {
            return {};
        }

        const newLeg = {
            origin: origin.id,
            destination: destination.id,
            trainOrigin: trainOriginStation ? trainOriginStation.id : null,
            trainDestination: trainDestinationStation ? trainDestinationStation.id : null,
            departure: legOriginStop?.time?.adjustedTime?.formatISOForAPI(),
            arrival: legDestinationStop?.time?.scheduledTime?.formatISOForAPI(),
            mode: serviceDetails.mode,
            toc: serviceDetails.toc ? serviceDetails.toc.id : null,
            reservable: serviceDetails.reservable || null,
            facilities: serviceDetails.facilities,
            rsid: serviceDetails.retailServiceId || '',
            tuid: serviceDetails.trainUid || '',
            isTemporaryTrain: serviceDetails.isTemporaryTrain,
            transferTime: leg.transferTime,
            callingPoints: callingPointIds,
        };

        _applySplitOriginAdjustment(newLeg, leg, legCallingPoints, callingPointIds, callingPoints, splitOriginNlc);
        _applySplitDestinationAdjustment(newLeg, leg, legCallingPoints, callingPointIds, callingPoints, splitDestinationNlc);

        return newLeg;
    });

    const { startIndex, endIndex } = _getSplitLegRange(newLegs, splitOriginNlc, splitDestinationNlc);
    return newLegs.slice(startIndex, endIndex + 1).filter((leg) => leg?.origin);
}

function serializeLegs(legs, callingPoints) {
    return legs.reduce((result, leg) => {
        const origin = callingPoints.getLegOrigin(leg);
        const destination = callingPoints.getLegDestination(leg);

        if (origin && destination) {
            result.push({
                origin: origin.id,
                destination: destination.id,
                trainOrigin: callingPoints.getTrainOriginStation(leg).id,
                trainDestination: callingPoints.getTrainDestinationStation(leg).id,
                departure: leg.getDepartureTime() && leg.getDepartureTime().formatISOForAPI(),
                arrival: leg.getArrivalTime() && leg.getArrivalTime().formatISOForAPI(),
                mode: leg.serviceDetails.mode,
                toc: leg.serviceDetails.getIn(['toc', 'id'], null),
                reservable: leg.serviceDetails.reservable || null,
                facilities: leg.serviceDetails.facilities.toJS(),
                rsid: leg.serviceDetails.retailServiceId || '',
                tuid: leg.serviceDetails.trainUid || '',
                isTemporaryTrain: leg.serviceDetails.isTemporaryTrain,
                transferTime: leg.transferTime
            });
        }
        return result;
    }, []);
}

export function serializeJourneyForSplitTickets (
    journey,
    reserve,
    callingPoints,
    splitOriginDestination,
) {
    if (!journey || !callingPoints) {
        return {
            reserve: false,
            legs: []
        };
    }
    const legs = serializeLegsForSplitTickets(journey.legs, callingPoints, splitOriginDestination);
    return {
        reserve,
        legs
    };
}

function serializeJourney (trip, direction) {
    //Season fares & day save require an outwardJourney for some reason, so we add an empty one
    if (!trip.needsJourney() && direction === JourneyDirections.OUTWARD) {
        return { reserve: false, legs: [] };
    }

    const service = trip.getServiceForDirection(direction);
    if (!service || typeof service.id !== 'string') { return { reserve: false, legs: [] }; }

    return CallingPointsActions.getFromService(service).then((callingPoints) => {
        const shouldReserve = trip.reservationPreferences.shouldReserveDirection(direction);

        const legs = serializeLegs(service.journey.legs, callingPoints);

        return {
            reserve: shouldReserve,
            legs,
        };
    });
}

function serializePassenger(trip) {
    return {
        name: trip.photocard.name,
        photocardId: trip.photocard.number
    };
}

function applyCommonTripFields(rawBody, trip, promo) {
    if (trip.bikes >= 1) {
        rawBody.bikes = trip.bikes;
    }

    rawBody.reservationPreferences = trip.reservationPreferences.toCodes();

    if (trip.isSmartcard()) {
        const smartcard = SmartcardStore.get('smartcard');
        rawBody.fares.passenger = { smartcardNumber: smartcard.isrn };
    }

    if (promo) {
        rawBody.fares.promoCode = promo.promoCode;
    }
}

function buildRegularSideTrip(body, { fareId, journey, callingPoints, reserve, referenceKey, referenceId, trip, promo }) {
    const rawBody = JSON.parse(JSON.stringify(body));

    rawBody.fares.outwardSingle = fareId;
    rawBody.outwardJourney = serializeJourneyForSplitTickets(
        journey,
        reserve,
        callingPoints,
        { originNlc: null, destinationNlc: null },
    );
    rawBody[referenceKey] = referenceId;

    applyCommonTripFields(rawBody, trip, promo);

    return rawBody;
}

function _getEmptySplitTripBody(splitSalt, trip) {
    return {
        splitSalt,
        fares: {
            outwardSingle: null,
            returnSingle: null,
            return: null,
            plusBus: {
                outward: {
                    origin: null,
                    destination: null,
                },
                return: {
                    origin: null,
                    destination: null,
                },
            },
            season: null,
            fareOnly: null,
            passenger: {
                photocardId: null,
            },
        },
        outwardJourney: {
            reserve: false,
            legs: [],
        },
        returnJourney: {
            reserve: false,
            legs: [],
        },
        itso: trip.isSmartcard(),
        isSeatsRequested: trip.isSeatsRequested,
    };
}

function _buildOutboundSplitTrips({
    body, trip, promo, outwardSplit, returnSplit,
    outboundService, returnService, outwardCallingPoints, returnCallingPoints,
    isOutboundValidForReturn, isOutboundValidForSingleReturn, reserveOutward, reserveInward,
}) {
    let trips = new List();

    for (const outboundFare of outwardSplit.fares) {
        const returnSplitFares = returnSplit?.fares || new List();
        const singleReturnFare = returnSplitFares.find((fare) =>
            fare.isValidForSingleReturn(outboundFare),
        );

        const rawBody = JSON.parse(JSON.stringify(body));

        if (isOutboundValidForReturn) {
            rawBody.fares.return = outboundFare.id;
        } else if (isOutboundValidForSingleReturn && singleReturnFare) {
            rawBody.fares.outwardSingle = outboundFare.id;
            rawBody.fares.returnSingle = singleReturnFare.id;
        } else {
            rawBody.fares.outwardSingle = outboundFare.id;
        }

        rawBody.outwardJourney = serializeJourneyForSplitTickets(
            outboundService.journey,
            reserveOutward,
            outwardCallingPoints,
            {
                originNlc: outboundFare?.splitInfo?.outwardOrigin?.nlc,
                destinationNlc: outboundFare?.splitInfo?.outwardDestination?.nlc,
            },
        );

        if (isOutboundValidForReturn || isOutboundValidForSingleReturn) {
            const splitOriginAndDestination = isOutboundValidForReturn
                ? {
                    originNlc: outboundFare?.splitInfo?.inwardOrigin?.nlc,
                    destinationNlc: outboundFare?.splitInfo?.inwardDestination?.nlc,
                }
                : {
                    originNlc: singleReturnFare?.splitInfo?.outwardOrigin?.nlc,
                    destinationNlc: singleReturnFare?.splitInfo?.outwardDestination?.nlc,
                };

            rawBody.returnJourney = serializeJourneyForSplitTickets(
                returnService.journey,
                reserveInward,
                returnCallingPoints,
                splitOriginAndDestination,
            );
        } else if (returnSplit?.fares?.size) {
            rawBody.returnSplitReferenceId = returnSplit.splitId;
        }

        applyCommonTripFields(rawBody, trip, promo);

        trips = trips.concat(rawBody);
    }

    let regularTrip = null;
    if (
        !isOutboundValidForReturn && !isOutboundValidForSingleReturn &&
        trip.fares.returnSingle && !returnSplit
    ) {
        regularTrip = buildRegularSideTrip(body, {
            fareId: trip.fares.returnSingle.id,
            journey: returnService.journey,
            callingPoints: returnCallingPoints,
            reserve: reserveInward,
            referenceKey: 'outwardSplitReferenceId',
            referenceId: outwardSplit.splitId,
            trip,
            promo,
        });
    }

    return { trips, regularTrip };
}

function _buildReturnOnlySplitTrips({
    body, trip, promo, outwardSplit, returnSplit,
    outboundService, outwardCallingPoints, returnService, returnCallingPoints,
    reserveOutward, reserveInward, isOutboundSplit,
}) {
    let trips = new List();

    for (const returnFare of returnSplit.fares) {
        const rawBody = JSON.parse(JSON.stringify(body));

        rawBody.outwardSplitReferenceId = outwardSplit?.splitId;
        rawBody.fares.outwardSingle = returnFare.id;
        rawBody.outwardJourney = serializeJourneyForSplitTickets(
            returnService.journey,
            reserveInward,
            returnCallingPoints,
            {
                originNlc: returnFare?.splitInfo?.outwardOrigin?.nlc,
                destinationNlc: returnFare?.splitInfo?.outwardDestination?.nlc,
            },
        );

        applyCommonTripFields(rawBody, trip, promo);

        trips = trips.concat(rawBody);
    }

    let regularTrip = null;
    if (!isOutboundSplit && trip.fares.outwardSingle) {
        regularTrip = buildRegularSideTrip(body, {
            fareId: trip.fares.outwardSingle.id,
            journey: outboundService.journey,
            callingPoints: outwardCallingPoints,
            reserve: reserveOutward,
            referenceKey: 'returnSplitReferenceId',
            referenceId: returnSplit.splitId,
            trip,
            promo,
        });
    }

    return { trips, regularTrip };
}

export const serialiseSplitTrips = async ({ trip, splitSalt }) => {
    if (!trip.fares.outwardSplit && !trip.fares.returnSplit) {
        return { splitTrips: new List(), regularTrip: null };
    }

    try {
        const body = _getEmptySplitTripBody(splitSalt, trip);

        const outboundService = trip.getServiceForDirection(JourneyDirections.OUTWARD);
        const returnService = trip.getServiceForDirection(JourneyDirections.INWARD);

        const { outwardCallingPoints, returnCallingPoints } = await CallingPointsActions.getCallingPointsForServices(
            outboundService,
            returnService,
        );

        const { outwardSplit, returnSplit } = trip.fares;
        const isOutboundSplit = !!outwardSplit;
        const isOutboundValidForReturn = outwardSplit?.isValidForReturn();
        const isOutboundValidForSingleReturn = outwardSplit?.isValidForSingleReturn(returnSplit);
        const reserveOutward = !!trip.reservationPreferences?.shouldReserveDirection(JourneyDirections.OUTWARD);
        const reserveInward = !!trip.reservationPreferences?.shouldReserveDirection(JourneyDirections.INWARD);
        const promo = PromoCodeStore.getActive();

        let trips = new List();
        let regularTrip = null;

        // At most one branch below ever produces a non-null regularTrip for a given trip:
        // _buildOutboundSplitTrips only sets one when `!returnSplit`, and the return-only
        // branch only runs when `returnSplit?.fares?.size` is truthy - so the second
        // assignment can never silently clobber a real value from the first. Keep that
        // invariant in mind if either guard condition changes.
        if (isOutboundSplit) {
            const outbound = _buildOutboundSplitTrips({
                body, trip, promo, outwardSplit, returnSplit,
                outboundService, returnService, outwardCallingPoints, returnCallingPoints,
                isOutboundValidForReturn, isOutboundValidForSingleReturn, reserveOutward, reserveInward,
            });
            trips = trips.concat(outbound.trips);
            regularTrip = outbound.regularTrip;
        }

        if (
            (!isOutboundSplit || (!isOutboundValidForReturn && !isOutboundValidForSingleReturn)) &&
            returnSplit?.fares?.size
        ) {
            const returnOnly = _buildReturnOnlySplitTrips({
                body, trip, promo, outwardSplit, returnSplit,
                outboundService, outwardCallingPoints, returnService, returnCallingPoints,
                reserveOutward, reserveInward, isOutboundSplit,
            });
            trips = trips.concat(returnOnly.trips);
            regularTrip = returnOnly.regularTrip;
        }

        return { splitTrips: trips, regularTrip };
    } catch (error) {
        console.error('Error serialising split trips', error);
        throw error;
    }
};

export async function serialiseTrip (trip) {
    const outwardFare = trip.getOutwardFare();
    const inwardFare = trip.getInwardFare();

    const shouldReserve = trip.reservationPreferences.shouldReserve();

    let body = {
        fares: {
            outwardSingle: (outwardFare && !outwardFare.isReturn()) ? outwardFare.id : null,
            returnSingle: inwardFare ? inwardFare.id : null,
            "return": (outwardFare && outwardFare.isReturn()) ? outwardFare.id : null,
            plusBus: serialisePlusBus(trip.getPlusBus()),
            season: trip.hasSeasonFare() ? trip.getSeasonFare().id : null,
            fareOnly: trip.hasFareOnlyFare() ? trip.getFareOnlyFare().id : null
        },
        outwardJourney: await serializeJourney(trip, JourneyDirections.OUTWARD),
        returnJourney: await serializeJourney(trip, JourneyDirections.INWARD),
        itso: trip.isSmartcard(),
        reservationPreferences: shouldReserve ? trip.reservationPreferences.toCodes() : null,
        isSeatsRequested: trip.isSeatsRequested,
    };

    if (trip.bikes >= 1) {
        body.bikes = trip.bikes;
    }

    if (trip.isSmartcard()) {
        const smartcard = SmartcardStore.get('smartcard');
        body.fares.passenger = { smartcardNumber: smartcard.isrn };
    } else if (trip.hasSeasonFare()) {
        body.fares.passenger = serializePassenger(trip);
    } else if (trip.hasRoversRangersFare() && trip.getRoversRangersFare().ticketType.isFamilyRanger() && trip.passengers[0]) {
        body.fares.passenger = { name: trip.passengers[0] };
    }

    const promo = PromoCodeStore.getActive();
    if (promo) {
        body.fares.promoCode = promo.promoCode;
    }

    return body;
}

async function serialiseOrder(order, trip, channel) {
    const parser = new UAParser(navigator.userAgent);
    const user = UserStore.getCurrent();
    const loggedIn = user.isLoggedIn();

    return {
        trip: await serialiseTrip(trip),
        channel,
        referrer: null,
        campaign: null,
        device: {
            customer: loggedIn ? `/customers/${user.id}` : null,
            deviceName: 'WEBSITE',
            udid: null,
            appVersion: appVersion.version,
            deviceType: parser.getDevice().type || 'unknown',
        },
        email: getEmailAddress({
            hasTransPennineStaffRoverTrip: trip ? trip.isTransPennineStaffRover() : false,
            loggedIn: user.isLoggedIn(),
            userEmail: user.email
        }),
    };
}

function serializeDelivery(order, disableAddress) {
    const { delivery } = order;
    let result = {
        name: delivery.displayName,
        deliveryMethodId: delivery.deliveryMethodId,
        price: delivery.price,
        fulfilmentMethod: delivery.fulfilmentMethod,
        address: delivery.isPostalDelivery() && !disableAddress ? delivery.getAddress().toJS() : null,
        collectionStartDate: null,
        collectionWindowDays: null
    };

    if (delivery.isCollection()) {
        result.collectionLocation = _.get(order, 'delivery.collectionLocation.nlc');
    }

    const enableETicketByPost = FeatureFlagStore.get('enableETicketByPost');
    if (delivery.isPostalDelivery() && enableETicketByPost) {
        result.isPrintedEticket = _.get(order, 'delivery.isPrintedEticket');
    }

    if (delivery.isSmartcard()) {
        let collectionlocation = order.getSmartcardCollectionLocation();
        if (_.isEmpty(collectionlocation)) {
            const smartcard = SmartcardStore.get('smartcard');
            // Fill collection location with origin station data if preload and collection location is "still" empty
            if (!_.isEmpty(smartcard) && smartcard.isAwaitingProduct()) {
                collectionlocation = order.getFirstOrigin();
            }
        }

        Object.assign(result, {
            collectionLocation: collectionlocation ? collectionlocation.nlc : null,
            collectionStartDate: order.getCollectionStartDate().format(),
            collectionWindowDays: delivery.collectionWindowDays
        });

    }

    return result;
}

function serializeLeadPassengers(trips) {
    return trips.reduce((result, trip) => {
        result[trip.id] = String(trip.selfPrintPassenger);
        return result;
    }, {});
}

function serializePhotocards(photocards) {
    return photocards.map(e => ({
        ticketId: e.ticketId,
        photocardDetails: {
            id: e.id,
            photocardNumber: e.number,
            linkCard: e.linkCard,
            nickName: e.nickname,
            cardHolder: {
                title: e.title,
                firstName: e.firstName,
                lastName: e.lastName,
                isOver16: e.isOver16,
                dob: e.isOver16 ? null : `${e.yearOfBirth}-${String(e.monthOfBirth).padStart(2, '0')}-${String(e.dayOfBirth).padStart(2, '0')}`,
                address: {
                    line1: e.addressLine1,
                    line2: e.addressLine2,
                    line3: e.addressLine3,
                    town: e.townCity,
                    country: e.country,
                    postCode: e.postcode,
                }
            }
        },
        smartcardDetails: null
    }));
}

function serializeSmartcards(trips, smartcard) {
    const user = UserStore.getCurrent();
    return trips.map(e => e.getTickets().map(ticket => ({
        customerId: user.id,
        ticketId: ticket.id,
        photocardDetails: null,
        smartcardDetails: {
            smartcardNumber: smartcard.isrn
        }
    }))).get(0);
}

function serializeLinkedSmartcards(trips, linkedSmartcards) {
    const user = UserStore.getCurrent();

    const associatedSmartcards = SmartcardStore.getAssociatedSmartcards();

    const passengers = trips.flatMap(e =>
        e
            .getTickets()
            .filter(ticket => !!linkedSmartcards.get(ticket.id))
            .map(ticket => {
                const smartcardNumber = linkedSmartcards.getIn([
                    ticket.id,
                    'smartcard',
                    'associatedSmartcardNumber',
                ]) || linkedSmartcards.getIn([
                    ticket.id,
                    'ticket',
                    'passenger',
                    'smartcardNumber',
                ]);

                const associatedCard = associatedSmartcards.find(card => card.associatedSmartcardNumber === smartcardNumber);
                const customerId = (associatedCard && associatedCard.customerId) || user.id;

                return {
                    customerId,
                    ticketId: ticket.id,
                    photocardDetails: null,
                    smartcardDetails: {
                        smartcardNumber: smartcardNumber || null,
                    }
                };
            })
    );

    return passengers;
}

const OrderService = {

    async create(order, trip, channel = "web") {
        try {
            const serializedOrder = await serialiseOrder(order, trip, channel);
            const response = await AuthenticatedAPIClient.post(dataUrl, serializedOrder);
            const deviceToken = response.result.deviceToken;
            return { order: resolve(response.result).as(Order), deviceToken };
        } catch (error) {
            throw throwCorrectOrderError(error);
        }
    },

    async addTrip(order, trip) {
        const url = `${dataUrl}/${order.id}/trips`;
        let output = order;

        try {
            const serializedTrip = await serialiseTrip(trip);
            const response = await AuthenticatedAPIClient.post(url, serializedTrip);

            if (!response?.result) {
                throw new EmptyOrderResponseError(response);
            }

            output = resolve(response.result).as(Order);
        } catch (error) {
            // A JSON content type with an empty body makes the API client's own
            // response.json() throw, before the result check above can run.
            throw throwCorrectOrderError(
                error instanceof SyntaxError ? new EmptyOrderResponseError(undefined, error) : error
            );
        }

        return output;
    },

    async deleteTrip(orderTrip) {
        try {
            const response = await AuthenticatedAPIClient.delete(orderTrip.id);
            return response;
        } catch (error) {
            throw throwCorrectOrderError(error);
        }
    },

    async updatePassengers(order, orderTrip, passengers) {
        const url = `${orderTrip.id}/passengers`;

        try {
            await AuthenticatedAPIClient.put(url, passengers.getRequestJSON());
            return true;
        } catch (error) {
            throw throwCorrectOrderError(error);
        }
    },

    async updateFulfilment(order, { disableAddress = false, linkedSmartcards = [] } = {}) {
        const url = `${dataUrl}/${order.id}`;
        const user = UserStore.getCurrent();

        let requestBody = {
            email: user.isLoggedIn()
                ? getEmailAddress({
                    hasTransPennineStaffRoverTrip: order.hasTransPennineStaffRoverTrip(),
                    loggedIn: user.isLoggedIn(),
                    userEmail: user.email
                })
                : order.email,
            telephoneNumber: user.isLoggedIn() ? user.telephoneNumber : order.telephoneNumber,
            delivery: serializeDelivery(order, disableAddress)
        };

        if (order.delivery.isPrintAtHome()) {
            requestBody.trips = serializeLeadPassengers(order.trips);
        }

        if (order.delivery.hasSeasonTrip(order.trips) && (order.delivery.isCollection() || order.delivery.isPostalDelivery())) {
            if (order.delivery.photocards.every(photocard => photocard.isComplete())) {
                requestBody.passengers = serializePhotocards(order.delivery.photocards);
            }
        }

        if (order.delivery.isSTicket() && order.delivery.sTicketPhotocard.isComplete()) {
            requestBody.passengers = serializePhotocards([order.delivery.sTicketPhotocard]);
        }

        if (order.delivery.isSmartcard()) {
            const smartcard = SmartcardStore.get('smartcard');

            const hasSmartcardAwaitingProduct = smartcard && smartcard.cardStatus === SmartcardStatus.AWAITING_PRODUCT;

            /*
                if no pre-encode, remove invalid smartcards from linked cards
                (pre-encode card appears in linkedSmartcards as an 'invalid' card that is always present, even when no pre-encode exists)
            */

            let validLinkedSmartcards = linkedSmartcards;

            if (!hasSmartcardAwaitingProduct) {
                linkedSmartcards.filter(x => x.get('smartcard') && x.get('smartcard').associatedSmartcardNumber);
            }

            const hasValidSmartcard = smartcard && (smartcard.isAwaitingProduct() || smartcard.isActiveAndAvailableForMoreProducts());

            // Fulfilment for PAH Smartcard only (includes pre-encode for PAH)
            if (hasValidSmartcard && (!validLinkedSmartcards || validLinkedSmartcards.size < 1)) {
                requestBody.passengers = serializeSmartcards(order.trips, smartcard);
            }

            // Linked cards scenario (includes using user's own Smartcard with other Linked cards as this is counted as a 'linked' card)
            if (validLinkedSmartcards && validLinkedSmartcards.size && validLinkedSmartcards.size > 0) {
                requestBody.passengers = serializeLinkedSmartcards(order.trips, validLinkedSmartcards);
            }
        }

        try {
            /*
                Don't update the order for Smartcard fulfilment if there are fewer passengers than tickets as
                this will never be valid and the PUT request will return an error
            */

            if (order.delivery.isSmartcard() && (!requestBody.passengers || (requestBody.passengers.size < order.getTickets().size))) {
                return order;
            }

            const response = await AuthenticatedAPIClient.put(url, requestBody);
            let updated = resolve(response.result).as(Order);

            if (order.delivery.isPostalDelivery() && order.delivery.address) {
                updated = updated.setIn(['delivery', 'address', 'toSave'], order.delivery.address.toSave);

                // Make the storedAddress available to the Payment Complete page
                // so we can update any postal favourites' delivery address
                // with a customer's saved address
                const { storedAddress } = order.delivery;
                const isUserAddressValid = storedAddress ? await AddressService.get(user, storedAddress.get('id')) : null;
                OrderStore.setPostalFulfilmentStoredAddress(storedAddress && isUserAddressValid ? storedAddress : null);
            }
            return updated;
        } catch (error) {
            throw throwCorrectOrderError(error);
        }
    },

    async get(id) {
        try {
            const response = await AuthenticatedAPIClient.get(dataUrl + '/' + id);
            if (response.result) {
                return resolve(response.result).as(Order);
            }
            return null;
        } catch (error) {
            if (error instanceof NotFoundError) {
                return new Order();
            }
            throw error;
        }
    },

    async getDeliveryOptions(id, smartcard) {
        const queryParams = FeatureFlagStore.get('enableSmartcardSeasonEnforcement') === true ? '?isEnforcementApplicable=true' : '';

        const invalidIdValues = ['null', 'undefined'];
        if (!id || invalidIdValues.includes(id)) {
            const error = new Error('OrderService.getDeliveryOptions: Invalid order id provided. id: ' + id);
            throw error;
        }

        const response = await AuthenticatedAPIClient.get(`/delivery/options/${id}${queryParams}`);

        const options = resolveAll(response.result).as(DeliveryOption);

        options.map(opt => {
            if (opt.errors) {
                opt.errors.map(err => {
                    if (err.GTRInteropFlexiFareFound) {
                        throw new InteropError();
                    }
                    return err;
                });
            }
        });

        const price = (option) => option.price;
        const method = (option) => option.getOrder();
        let sorted = options.sortBy(price).sortBy(method);
        if (FeatureFlagStore.get('disableSelfPrint')) {
            sorted = sorted.filter(option => !option.isPrintAtHome());
        }
        if (FeatureFlagStore.get('disableETickets')) {
            sorted = sorted.filter(option => !option.isETicket());
        }
        if (FeatureFlagStore.get('disableTOD')) {
            sorted = sorted.filter(option => !option.isCollection());
        }
        if (FeatureFlagStore.get('disableCCST')) {
            sorted = sorted.filter(option => !option.isPostalDelivery());
        }

        if (!FeatureFlagStore.get('enableProductPreload') && smartcard && smartcard.isAwaitingProduct()) {

            const deliveryWithMockStatus = sorted.map(deliveryMethod => {
                if (deliveryMethod.fulfilmentMethod === FulfilmentType.SMARTCARD) {
                    if (deliveryMethod.errors) {
                        return deliveryMethod.set('errors', deliveryMethod.errors.concat({ DisabledPreEncodeError: {} }));
                    } else {
                        return deliveryMethod.set('errors', [{ DisabledPreEncodeError: {} }]);
                    }
                } else {
                    return deliveryMethod;
                }
            });

            return deliveryWithMockStatus;
        }

        return sorted;
    },


    delete(order) {
        return AuthenticatedAPIClient.delete(dataUrl + '/' + order.id);
    },

    async updatePlusBus(order, orderTrip, trip) {
        const url = `${dataUrl}/${order.id}/trips/${orderTrip.getCleanId()}/plusbus`;
        const body = {
            plusBus: serialisePlusBus(trip.getPlusBus())
        };
        try {
            const response = await AuthenticatedAPIClient.put(url, body);
            return resolve(response.result).as(Order);
        } catch (error) {
            throw throwCorrectOrderError(error);
        }
    },

    getTicketUrl(order, ticket) {
        return `/orders/${order.id}/tickets/${ticket.id}.pdf`;
    },

    async deleteSTicketPhoto(userId) {
        try {
            await AuthenticatedAPIClient.delete(`stickets/${userId}/image`);
        } catch (error) {
            throw error;
        }
    },

    async updateSTicketPhoto(userId, binary) {
        try {
            const response = await AuthenticatedAPIClient.postForm(`/stickets/${userId}/image`, {
                photo: new Blob([binary])
            });
            return resolve(response.result);
        } catch (error) {
            throw error;
        }
    },

    async sendSTicketPhotoChangeRequest(userId, binary) {
        try {
            const response = await AuthenticatedAPIClient.postForm(`/stickets/${userId}/image/change`, {
                photo: new Blob([binary])
            });
            return resolve(response.result);
        } catch (error) {
            throw error;
        }
    },

    async getSTicketPhoto(userId) {
        try {
            // if there's no image added by user - api returns 404, so there's no need to throw an error
            const response = await AuthenticatedAPIClient.get(`/stickets/${userId}/image`, {}, { validErrorStatuses: [404] });
            return response;
        } catch (error) {
            throw error;
        }
    },

    async linkToAccount(token, email) {
        const url = `${dataUrl}/linking`;

        const requestBody = { email };
        const requestOptions = { headers: { 'x-jwt-token': token } };

        try {
            const response = await AuthenticatedAPIClient.post(url, requestBody, requestOptions);
            return resolve(response.result);
        } catch (error) {
            throw error;
        }
    },

    /*
        Legacy Trainhugger discount code scheme

        Leaving in place in case this is ever repurposed
        for another TOC or brand
    */
    async applyDiscountCode(order, discountCode) {
        const url = `${dataUrl}/${order.id}/discount/trainhugger`;
        const requestBody = { discountCode };

        try {
            const response = await AuthenticatedAPIClient.put(url, requestBody);
            return resolve(response.result).as(Order);
        } catch (error) {
            throw error;
        }
    },

    /*
        Legacy Trainhugger discount code scheme

        Leaving in place in case this is ever repurposed
        for another TOC or brand
    */
    async removeDiscountCode(order, discountCode) {
        const url = `${dataUrl}/${order.id}/discount/${discountCode}/trainhugger`;

        try {
            const response = await AuthenticatedAPIClient.delete(url);
            return resolve(response.result).as(Order);
        } catch (error) {
            throw error;
        }
    },

    async applyVoucherCode(order, voucherCode) {
        const url = `/v2/orders/${order.id}/discount-vouchers/assign?code=${encodeURIComponent(voucherCode)}`;
        const response = await AuthenticatedAPIClient.post(url);
        return AppliedVoucher.fromJSON(response);
    },

    async removeVoucherCode(order, voucherCode) {
        const url = `/v2/orders/${order.id}/discount-vouchers/unassign?code=${encodeURIComponent(voucherCode)}`;
        await AuthenticatedAPIClient.post(url);
    },

    async getDiscountVouchers(orderId) {
        const response = await AuthenticatedAPIClient.get(`/v2/orders/${orderId}/discount-vouchers`);
        return List(response).map((item) => Voucher.fromJSON(item)); // eslint-disable-line new-cap
    },

    async lock(order) {
        return AuthenticatedAPIClient.put(`/orders/${order.id}/lock`);
    },

    async unlock(order, options = {}) {
        const keys = Object.keys(options);
        const query = keys.length
            ? `?${keys.map(k => `${encodeURIComponent(k)}=${encodeURIComponent(options[k])}`).join('&')}`
            : '';
        return AuthenticatedAPIClient.delete(`/orders/${order.id}/lock${query}`);
    },

    async loadChangeovers(order, customerId) {
        const response = await AuthenticatedAPIClient.get(`/changeover/order/${order.id}/customer/${customerId}`);
        return resolveAll(response).as(Changeover);
    },

    async validateRewardCode({ orderId, customerId, rewardCode }) {
        return AuthenticatedAPIClient.post(`/rewards/${orderId}/validate`, {
            customerId,
            rewardCode
        });
    },

    async downloadExpenseReceipt(orderId) {
        return AuthenticatedAPIClient.get(
            `/v2/orders/${orderId}/receipt`,
            {},
            {
                headers: { accept: 'application/pdf' },
                responseType: 'blob',
            },
        );
    },

    async downloadMultipleExpenseReceipts (customerId, from, to) {
        return AuthenticatedAPIClient.get(
            `/v2/orders/receipts`,
            { customerId, from, to },
            {
                headers: { accept: 'application/zip' },
                responseType: 'blob',
            },
        );
    }
};

export default OrderService;
